import React, { useState, FormEvent } from 'react';
import {
    TextField,
    IconButton,
    Button,
    Box,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import { ButtonGroupOptions } from '../../pages/assistant/ButtonGroupOptions';
import { useDropZoneField } from '../general/fields/dropzoneField/hook';
import { fileToBase64 } from '../../../shared/utils/files';
import { FileExplorer } from './sharepoint/FileExplorer';
import { AdvancedMenu } from './AdvancedMenu';
import { SharepointItem } from '../../../shared/types/sharepoint';

interface Props {
    onSendMessage: (message: string, files: any) => void;
    placeholder?: string;
    disableCorrections?: boolean;
    disable?: boolean;
    isAdvancedMode: boolean;
    anchorRef: React.RefObject<HTMLDivElement>;
    options: {
        label: string;
        tooltip: string;
    }[];
    selectedIndex: number;
    openOptionButton: boolean;
    handleClose: (event: Event) => void;
    handleMenuItemClick: (index: number) => void;
    handleToggle: () => void;
    isVisibleButtons: boolean;
    adittionalShareponitItems?: SharepointItem[];
    adittionalLocalItems?: {
        id: number;
        file: any;
    }[];
}

export const TextMessageBox = ({
    onSendMessage,
    placeholder,
    disableCorrections = false,
    disable = false,
    isAdvancedMode = false,
    anchorRef,
    options,
    selectedIndex,
    openOptionButton,
    handleClose,
    handleMenuItemClick,
    handleToggle,
    isVisibleButtons,
}: Props) => {
    const [message, setMessage] = useState('');
    const [isSharepointAuth, setIsSharepointAuth] = useState(false);
    const [sharepointModalOpen, setSharepointModalOpen] = useState(false);
    const [sharepointItems, setSharepointItems] = useState<SharepointItem[]>(
        []
    );

    const acceptedFiles = '.xlsx ,.doc, .docx, .pdf';
    const { updateFiles, files, setFiles, removeFile } = useDropZoneField({
        accept: acceptedFiles,
        maxFileSize: 10 * 1024 * 1024,
    });

    const send = async () => {
        const sanitizedMessage =
            message.trim().length === 0 ? 'Analizar archivo(s)' : message;
        const data: any = [];
        for await (const file of files) {
            const res = await fileToBase64(file.file);
            if (res) data.push(res);
        }
        sharepointItems.forEach((f) =>
            data.push({
                id: f.id,
                name: f.name,
                isSharepoint: true,
                isFolder: !!f.folder,
                driveId: f?.parentReference?.driveId || f?.driveId, // MyDrive or SharePoint
            })
        );
        onSendMessage(sanitizedMessage, data);
        setMessage('');
        setFiles([]);
        setSharepointItems([]);
    };
    const handleSendMessage = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        send();
    };
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <>
            <Box
                component="form"
                onSubmit={handleSendMessage}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    borderRadius: 2,
                    backgroundColor: 'white',
                    px: 2,
                    py: 1,
                }}
            >
                {(files.length > 0 || sharepointItems.length > 0) && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            alignItems: 'center',
                            px: 1,
                        }}
                    >
                        {files.map(({ id, file }) => (
                            <Box
                                key={id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    bgcolor: '#f5f5f5',
                                    minWidth: 0,
                                }}
                            >
                                {file.type.startsWith('image/') ? (
                                    <Box
                                        component="img"
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            mr: 1,
                                        }}
                                    />
                                ) : (
                                    <AttachFileIcon
                                        sx={{ fontSize: 20, mr: 1 }}
                                    />
                                )}
                                <Box
                                    sx={{
                                        maxWidth: 120,
                                        fontSize: 12,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {file.name}
                                </Box>
                                <IconButton
                                    onClick={() => removeFile(id)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {sharepointItems.map(({ id, name, folder }) => (
                            <Box
                                key={id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    bgcolor: 'common.sidebar',
                                    minWidth: 0,
                                }}
                            >
                                {folder ? (
                                    <FolderIcon
                                        sx={{
                                            fontSize: 20,
                                            mr: 1,
                                            color: '#ccc',
                                        }}
                                    />
                                ) : (
                                    <AttachFileIcon
                                        sx={{
                                            fontSize: 20,
                                            mr: 1,
                                        }}
                                    />
                                )}
                                <Box
                                    sx={{
                                        maxWidth: 120,
                                        fontSize: 12,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {name}
                                </Box>
                                <IconButton
                                    onClick={() =>
                                        setSharepointItems((prev) =>
                                            prev.filter((f) => f.id !== id)
                                        )
                                    }
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <TextField
                            type="text"
                            autoFocus
                            name="message"
                            fullWidth
                            variant="outlined"
                            placeholder={placeholder}
                            autoComplete={disableCorrections ? 'off' : 'on'}
                            autoCorrect={disableCorrections ? 'off' : 'on'}
                            spellCheck={disableCorrections ? 'false' : 'true'}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            multiline
                            maxRows={2}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    px: 1.5,
                                },
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {isAdvancedMode && (
                                                <Tooltip
                                                    title="Adjuntar archivos"
                                                    arrow
                                                >
                                                    <div>
                                                        <IconButton
                                                            component="label"
                                                            size="small"
                                                            onClick={
                                                                handleClick
                                                            }
                                                        >
                                                            <AddIcon />
                                                        </IconButton>
                                                        <AdvancedMenu
                                                            anchorEl={anchorEl}
                                                            setAnchorEl={
                                                                setAnchorEl
                                                            }
                                                            setSharepointModalOpen={
                                                                setSharepointModalOpen
                                                            }
                                                            updateFiles={
                                                                updateFiles
                                                            }
                                                            isSharepointAuth={
                                                                isSharepointAuth
                                                            }
                                                            setIsSharepointAuth={
                                                                setIsSharepointAuth
                                                            }
                                                            acceptedFiles={
                                                                acceptedFiles
                                                            }
                                                        />
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {isVisibleButtons && (
                                                <ButtonGroupOptions
                                                    anchorRef={anchorRef}
                                                    options={options}
                                                    selectedIndex={
                                                        selectedIndex
                                                    }
                                                    openOptionButton={
                                                        openOptionButton
                                                    }
                                                    handleClose={handleClose}
                                                    handleMenuItemClick={
                                                        handleMenuItemClick
                                                    }
                                                    handleToggle={handleToggle}
                                                />
                                            )}
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box sx={{ ml: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={
                                <i className="fa-regular fa-paper-plane"></i>
                            }
                            disabled={disable}
                        >
                            Enviar
                        </Button>
                    </Box>
                </Box>
            </Box>
            <FileExplorer
                open={sharepointModalOpen}
                onClose={() => setSharepointModalOpen(false)}
                selectedItems={sharepointItems}
                setIsSharepointAuth={setIsSharepointAuth}
                setSharepointItems={setSharepointItems}
                setSharepointModalOpen={setSharepointModalOpen}
            />
        </>
    );
};
